import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";

type VerificationStatus = "idle" | "uploading" | "verifying" | "success" | "failed";

interface VerificationResult {
  success: boolean;
  confidence?: number;
  message?: string;
}

export const useVerification = () => {
  const { user, profile, refreshProfile } = useAuthContext();
  const [status, setStatus] = useState<VerificationStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [confidence, setConfidence] = useState<number | null>(null);

  // Start verification process with selfie
  const verifyWithSelfie = useCallback(async (selfieFile: File): Promise<VerificationResult> => {
    if (!user || !profile) {
      return { success: false, message: "Not authenticated" };
    }

    if (!profile.photo_url) {
      return { success: false, message: "Please upload a profile photo first" };
    }

    setStatus("uploading");
    setError(null);

    try {
      // Upload selfie to storage
      const fileExt = selfieFile.name.split(".").pop();
      const filePath = `${user.id}/verification-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("profile-photos")
        .upload(filePath, selfieFile, { upsert: true });

      if (uploadError) {
        throw new Error("Failed to upload selfie");
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("profile-photos")
        .getPublicUrl(filePath);

      const selfieUrl = urlData.publicUrl;

      // Create verification request
      const { error: insertError } = await supabase
        .from("verification_requests")
        .insert({
          user_id: user.id,
          selfie_url: selfieUrl
        });

      if (insertError) {
        // Check if there's already a pending request
        if (insertError.code === "23505") {
          throw new Error("You already have a pending verification request");
        }
        throw new Error("Failed to create verification request");
      }

      setStatus("verifying");

      // Call edge function for AI verification
      const { data, error: funcError } = await supabase.functions.invoke(
        "verify-profile",
        {
          body: {
            selfieUrl,
            profilePhotoUrl: profile.photo_url,
            userId: user.id
          }
        }
      );

      if (funcError) {
        console.error("Verification error:", funcError);
        throw new Error("Verification service unavailable");
      }

      if (data.verified) {
        setStatus("success");
        setConfidence(data.confidence);
        await refreshProfile();
        return { 
          success: true, 
          confidence: data.confidence,
          message: "Verification successful! You're now verified." 
        };
      } else {
        setStatus("failed");
        setError(data.reason || "Photos don't match");
        return { 
          success: false, 
          confidence: data.confidence,
          message: data.reason || "Verification failed. Please try again with a clearer photo."
        };
      }
    } catch (err) {
      const message = (err as Error).message;
      setStatus("failed");
      setError(message);
      return { success: false, message };
    }
  }, [user, profile, refreshProfile]);

  // Check verification status
  const checkStatus = useCallback(async () => {
    if (!user) return null;

    const { data } = await supabase
      .from("verification_requests")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    return data;
  }, [user]);

  const reset = useCallback(() => {
    setStatus("idle");
    setError(null);
    setConfidence(null);
  }, []);

  return {
    status,
    error,
    confidence,
    verifyWithSelfie,
    checkStatus,
    reset,
    isVerified: profile?.is_verified ?? false
  };
};
